import { useEffect, useRef } from 'react'
import ShuttleRouteMotion from './ShuttleRouteMotion'
import { REGISTRATION_HERO_SURFACE } from '../../utils/heroStyles'
import { HOME_SHUTTLE_APPROACHING_MAX_MINUTES } from '../../utils/homeTimePolicy'
import { shuttleData } from '../../data/shuttleData'
import nightSkyBg from '../../assets/night-sky-bg.jpg'

function parseShuttleDirection(subLine = '', statusLabel = '') {
  if (/학교\s*→\s*병점역|하교|귀가|집\s*가/.test(subLine) || /집 가는 셔틀/.test(statusLabel)) {
    return { fromLabel: '학교', toLabel: '병점역' }
  }
  return { fromLabel: '병점역', toLabel: '학교' }
}

function timeStrToMin(time) {
  const [h, m] = String(time ?? '').split(':').map(Number)
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null
}

// 공식 시각이 막 지난(10분 이내) 경우를 찾는다 — shuttle.nextTime은 항상 다음
// "미래" 시각만 가리키므로(지난 시각은 상위 로직이 건너뜀), 같은 노선의 시간표를
// 다시 훑어 가장 가까운 과거 공식 시각이 10분 이내인지 확인한다.
function findRecentlyPassedOfficialMin(routeName, nowMin) {
  if (!routeName || nowMin == null) return null
  const route = shuttleData.routes?.find(r => r.routeName === routeName)
  const times = route?.stops?.[0]?.times ?? []
  let best = null
  for (const time of times) {
    const depMin = timeStrToMin(time)
    if (depMin == null || depMin > nowMin) continue
    if (nowMin - depMin > 10) continue
    if (best == null || depMin > best) best = depMin
  }
  return best
}

// 셔틀은 공식 시간 기준 ±5분 정도 변동이 있을 수 있어, 정확한 도착 예측처럼
// 보이는 카운트다운 대신 운행 상태 표현으로 안내한다.
function getShuttlePillLabel({ recentPassedMin, minsToShuttle, nowMin }) {
  if (recentPassedMin != null) {
    const elapsed = nowMin - recentPassedMin
    return elapsed <= HOME_SHUTTLE_APPROACHING_MAX_MINUTES ? '지금 대기 권장' : '지연/출발 가능'
  }
  if (minsToShuttle == null) return '다음 셔틀 보기'
  if (minsToShuttle <= HOME_SHUTTLE_APPROACHING_MAX_MINUTES) return '지금 대기 권장'
  return '곧 운행'
}

export function buildHomeHeroMeta({ dateStr, weekStr, profile, previewMode = false }) {
  const studentInfo = [
    profile?.department,
    profile?.grade ? `${profile.grade}학년` : null,
  ].filter(Boolean).join(' ')
  return [
    dateStr,
    studentInfo || null,
    weekStr,
    previewMode ? '미리보기' : null,
  ].filter(Boolean).join(' · ')
}

export default function HomeUnifiedHero({
  isDark,
  motionMode = 'planner',
  variant,
  urgent = false,
  nightSkyHero = false,
  metaLine,
  displayName = '예성',
  statusLabel,
  subLine,
  details = [],
  ctaLabel = '시간표 보기',
  onClick,
  shuttleTime,
  minsToShuttle,
  shuttleRouteName,
  shuttleBusCount = 1,
  shuttleFollowingTime = null,
  registrationLabel,
  registrationBig,
  registrationCaption,
}) {
  const canvasRef = useRef(null)
  // 어두운 히어로 카드 판정 — 배경이 딥 네이비(HERO_SURFACE.bgDark)면 항상 true.
  // .hero-effect-night 조상 체인에 의존하지 않고 카드 자체에 dark 클래스를 붙이기 위함.
  const isDarkHeroCard =
    isDark ||
    String(motionMode).toLowerCase().includes('night') ||
    String(motionMode).toLowerCase().includes('done')

  const isRegistration = variant === 'registration'
  const ctaBg = isRegistration
    ? (isDark ? REGISTRATION_HERO_SURFACE.ctaDark : REGISTRATION_HERO_SURFACE.ctaLight)
    : undefined
  const heroKey = `${motionMode}-${statusLabel ?? ''}-${subLine ?? ''}-${details.join('|')}`
  const safeDetails = details.filter(Boolean).slice(0, 2)

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick?.()
    }
  }

  // variant가 명시되면 그걸 따르고(ClassicHeroCard의 모드 매핑), 없으면
  // motionMode로부터 유추한다(TodayHomeView 등 variant를 안 넘기는 호출부 호환).
  const isShuttle = variant ? variant === 'data' : motionMode === 'shuttle'
  const shuttleNowMin = isShuttle ? (() => { const now = new Date(); return now.getHours() * 60 + now.getMinutes() })() : null
  const shuttleRecentPassedMin = isShuttle ? findRecentlyPassedOfficialMin(shuttleRouteName, shuttleNowMin) : null
  const shuttlePillLabel = isShuttle
    ? getShuttlePillLabel({ recentPassedMin: shuttleRecentPassedMin, minsToShuttle, nowMin: shuttleNowMin })
    : null
  const shuttleAnchorTime = shuttleRecentPassedMin != null
    ? `${String(Math.floor(shuttleRecentPassedMin / 60)).padStart(2, '0')}:${String(shuttleRecentPassedMin % 60).padStart(2, '0')}`
    : shuttleTime

  useEffect(() => {
    if (!nightSkyHero) return undefined

    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return undefined

    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (media?.matches) return undefined

    let canceled = false
    let rafId = 0
    let width = 0
    let height = 0
    let lastNow = performance.now()
    let nextSpawnAt = performance.now() + 1200
    let elapsed = 0
    let meteors = []

    const twinkles = Array.from({ length: 60 }, () => ({
      x: Math.random(),
      y: Math.random() * 0.82,
      r: 0.4 + Math.random() * 0.6,
      base: 0.25 + Math.random() * 0.45,
      phase: Math.random() * Math.PI * 2,
      speed: 0.6 + Math.random() * 1.4,
    }))

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      width = rect.width
      height = rect.height
      canvas.width = Math.floor(width * dpr)
      canvas.height = Math.floor(height * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const spawnMeteor = () => {
      if (meteors.length >= 2 || !width || !height) return

      const x = width * (0.12 + Math.random() * 0.82)
      const y = height * (0.02 + Math.random() * 0.26)
      const angle = Math.PI * (0.72 + Math.random() * 0.10)
      const speed = 5.8 + Math.random() * 3.2
      const vx = Math.cos(angle) * speed
      const vy = Math.sin(angle) * speed

      meteors.push({
        x,
        y,
        vx,
        vy,
        life: 1,
        maxLife: 1,
        length: 70 + Math.random() * 45,
        size: 1.2 + Math.random() * 0.8,
      })
    }

    const nextDelay = () => 3000 + Math.random() * 4500

    const animate = now => {
      if (canceled) return

      const delta = Math.min(32, now - lastNow)
      const frameScale = delta / 16.67
      lastNow = now
      elapsed += delta / 1000

      ctx.clearRect(0, 0, width, height)

      twinkles.forEach(star => {
        const alpha = star.base * (0.5 + 0.5 * Math.sin(elapsed * star.speed + star.phase))
        ctx.fillStyle = `rgba(255,255,255,${alpha})`
        ctx.beginPath()
        ctx.arc(star.x * width, star.y * height, star.r, 0, Math.PI * 2)
        ctx.fill()
      })

      if (now >= nextSpawnAt) {
        spawnMeteor()
        nextSpawnAt = now + nextDelay()
      }

      for (let i = meteors.length - 1; i >= 0; i -= 1) {
        const meteor = meteors[i]
        meteor.x += meteor.vx * frameScale
        meteor.y += meteor.vy * frameScale
        meteor.life -= 0.018 * frameScale

        if (
          meteor.x < -120 ||
          meteor.x > width + 120 ||
          meteor.y > height + 120 ||
          meteor.life <= 0
        ) {
          meteors.splice(i, 1)
          continue
        }

        const alpha = Math.max(0, meteor.life / meteor.maxLife)
        const tailX = meteor.x - meteor.vx * (meteor.length / 8)
        const tailY = meteor.y - meteor.vy * (meteor.length / 8)
        const gradient = ctx.createLinearGradient(meteor.x, meteor.y, tailX, tailY)
        gradient.addColorStop(0, `rgba(255,255,255,${0.95 * alpha})`)
        gradient.addColorStop(0.35, `rgba(190,215,255,${0.55 * alpha})`)
        gradient.addColorStop(1, 'rgba(190,215,255,0)')
        ctx.strokeStyle = gradient
        ctx.lineWidth = 1.2
        ctx.lineCap = 'round'
        ctx.beginPath()
        ctx.moveTo(meteor.x, meteor.y)
        ctx.lineTo(tailX, tailY)
        ctx.stroke()
        ctx.fillStyle = `rgba(255,255,255,${0.9 * alpha})`
        ctx.beginPath()
        ctx.arc(meteor.x, meteor.y, meteor.size, 0, Math.PI * 2)
        ctx.fill()
      }

      rafId = window.requestAnimationFrame(animate)
    }

    resize()
    window.addEventListener('resize', resize)
    rafId = window.requestAnimationFrame(animate)

    return () => {
      canceled = true
      window.cancelAnimationFrame(rafId)
      window.removeEventListener('resize', resize)
      meteors.length = 0
    }
  }, [nightSkyHero])

  return (
    <div
      role="button"
      tabIndex={0}
      className={`home-dashboard-card home-aurora-card premium-hero-card home-unified-hero standard-hero hero-layout--${isRegistration ? 'registration' : 'basic'} premium-hero-${motionMode}${urgent ? ' premium-hero-urgent' : ''}${isDarkHeroCard ? ' premium-hero-card--dark' : ''}${nightSkyHero ? ' premium-hero-card--night-sky' : ''}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      {nightSkyHero && (
        <>
          <span
            className="premium-hero-night-photo"
            aria-hidden="true"
            style={{ backgroundImage: `url(${nightSkyBg})` }}
          />
          <span className="premium-hero-night-scrim" aria-hidden="true" />
          <canvas ref={canvasRef} className="premium-hero-night-canvas" aria-hidden="true" />
        </>
      )}
      {isDarkHeroCard && !nightSkyHero && (
        <span className="premium-hero-meteor" aria-hidden="true" />
      )}
      <div className="suguide-ambient-layer" aria-hidden="true" />
      <span className="premium-hero-route-line" aria-hidden="true" />
      <span className="premium-hero-route-light" aria-hidden="true" />
      <span className="premium-hero-steam premium-hero-steam-a" aria-hidden="true" />
      <span className="premium-hero-steam premium-hero-steam-b" aria-hidden="true" />
      <span className="premium-hero-steam premium-hero-steam-c" aria-hidden="true" />

      {/* ── 공통 골격: Top(메타/제목) → Main(핵심 정보) → Supporting → CTA ──
          variant는 각 Zone의 콘텐츠/스타일만 바꾸고, 세로 기준은 공유한다. */}
      <div key={`copy-${heroKey}`} className="home-aurora-content premium-hero-copy home-unified-hero__copy">
        <div className="home-unified-hero__top-zone" data-zone="top">
          {metaLine && <p className="home-unified-hero__meta">{metaLine}</p>}
          {isRegistration ? (
            <h1 className="registration-hero__label home-unified-hero__title">
              {registrationLabel}
            </h1>
          ) : isShuttle ? (
            <h1 className="shuttle-hero__status home-unified-hero__title">
              {shuttlePillLabel}
            </h1>
          ) : (
            <h1 className="home-unified-hero__title">
              {displayName ? `${displayName}님,` : '안녕하세요,'}<br />{statusLabel}
            </h1>
          )}
        </div>

        <div className="home-unified-hero__main-zone" data-zone="main">
          {isShuttle ? (
            shuttleAnchorTime ? (
              <p className="shuttle-hero__time">{shuttleAnchorTime}</p>
            ) : (
              <p className="shuttle-hero__time--fallback">오늘 운행 정보 없음</p>
            )
          ) : isRegistration ? (
            <p className="registration-hero__value">{registrationBig}</p>
          ) : (
            <p className="home-unified-hero__sub">{subLine}</p>
          )}
        </div>

        <div className="home-unified-hero__supporting-zone" data-zone="supporting">
          {isShuttle ? (
            <div className="home-unified-hero__data-extra">
              {shuttleAnchorTime && <p className="shuttle-hero__time-label">전후 운행</p>}
              {shuttleAnchorTime && <p className="home-unified-hero__shuttle-note">공식 시간 기준</p>}
              {(() => {
                const { fromLabel, toLabel } = parseShuttleDirection(subLine, statusLabel)
                return (
                  <ShuttleRouteMotion
                    from={fromLabel}
                    to={toLabel}
                    departureTime={shuttleTime}
                    minsUntilDeparture={minsToShuttle}
                    busCount={shuttleBusCount}
                    followingDepartureTime={shuttleFollowingTime}
                    isEnded={!shuttleTime || minsToShuttle == null}
                    isDark={isDark}
                  />
                )
              })()}
            </div>
          ) : isRegistration ? (
            <>
              {registrationCaption && <p className="registration-hero__caption">{registrationCaption}</p>}
              {subLine && <p className="home-unified-hero__sub">{subLine}</p>}
            </>
          ) : (
            safeDetails.length > 0 && (
              <div className="home-unified-hero__details">
                {safeDetails.map((detail, index) => <p key={index}>· {detail}</p>)}
              </div>
            )
          )}
        </div>

        {ctaLabel && (
          <div className="home-unified-hero__cta-zone" data-zone="cta">
            <button
              type="button"
              className="home-pressable-button premium-hero-cta home-unified-hero__cta"
              onClick={e => { e.stopPropagation(); onClick?.() }}
              style={{ background: ctaBg }}
            >
              {ctaLabel}
            </button>
          </div>
        )}
      </div>

    </div>
  )
}
